import type { HearingLossRecipe } from 'common/interfaces/hearingLoss';
import { DateTime } from '@ksyos/date-time';
import { Box, Button, FileTypeIcon, Link } from '@ksyos/design-system';
import { useParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { downloadHearingLossRecipe, getHearingLossRecipes } from '../../../services/api/hearingLossApiService';
import { getProfessionName } from '../../../services/hearingLossService';
import { translateKey } from '../../../translate';
import { HeroText } from '../../elements/atoms/heroText/HeroText';
import { PageTitle } from '../../elements/atoms/pageTitle/PageTitle';
import { Paragraph } from '../../elements/atoms/paragraph/Paragraph';
import { ParagraphTitle } from '../../elements/atoms/paragraphTitle/ParagraphTitle';
import { Article } from '../../elements/molecules/article/article';
import { Container } from '../../elements/molecules/container/Container';
import { Content } from '../../elements/molecules/content/Content';
import { Main } from '../../elements/molecules/main/Main';
import { PageSection } from '../../elements/molecules/pageSection/PageSection';
import { Footer } from '../../elements/organisms/footer/Footer';
import { Header } from '../../elements/organisms/header/Header';
import { PageWrapper } from '../../elements/organisms/pageWrapper/PageWrapper';

export function HearingLossDownloadRecipe() {
    const { processActivityId } = useParams();
    const [hearingLossRecipe, setHearingLossRecipe] = useState<HearingLossRecipe>();

    useEffect(() => {
        loadHearingLossRecipe();
    }, []);

    const profession = getProfessionName(hearingLossRecipe?.profession);
    const performedDate = hearingLossRecipe?.performedDatetime
        ? DateTime.from(hearingLossRecipe?.performedDatetime).format('DD-MM-YYYY')
        : '';

    return (
        <PageWrapper>
            <Header
                backButtonText={translateKey('common.home')}
                backUrl="/"
                hasBackbutton
                hasLogoutButton
            />
            <Main>
                <Container isFluidOnMobile>
                    <PageSection>
                        <Article>
                            <PageTitle>Recept hoorhulpmiddel</PageTitle>
                            <HeroText>
                                Je hebt van de Ksyos {profession} een recept voor een hoortoestel of ander
                                hoorhulpmiddel gekregen. Lees hier wat je zelf regelt.
                            </HeroText>
                        </Article>
                    </PageSection>
                    <PageSection bgColor="white">
                        <Article>
                            <Content>
                                <ParagraphTitle>1. Download het recept</ParagraphTitle>
                                <Paragraph>
                                    Om je hoorhulpmiddel vergoed te krijgen door de zorgverzekeraar, heeft je audicien
                                    een recept nodig.
                                </Paragraph>
                                <Box
                                    alignItems="center"
                                    display="flex"
                                    flexDirection="column"
                                    gap={4}
                                >
                                    <FileTypeIcon
                                        icon="pdf"
                                        width={44}
                                    />
                                    <Paragraph>Recept hoorhulpmiddel {performedDate}.pdf</Paragraph>
                                </Box>
                                <Button
                                    color="secondary"
                                    onClick={downloadRecipe}
                                    width="full"
                                >
                                    Download recept
                                </Button>
                            </Content>
                        </Article>
                    </PageSection>
                    <PageSection bgColor="white">
                        <Article>
                            <Content>
                                <ParagraphTitle>2. Maak een afspraak bij een audicien</ParagraphTitle>
                                <Paragraph>
                                    Je kunt met dit recept naar elke audicien om een goed hoorhulpmiddel aan te laten
                                    meten. De website Hoorwijzer kan helpen bij het zoeken van een goede audicien bij
                                    jou in de buurt.
                                </Paragraph>
                                <Link
                                    color="secondary"
                                    href="https://www.hoorwijzer.nl/audiciens/"
                                    iconLeft="external"
                                    openInNewTab={true}
                                    variant="externalLink"
                                >
                                    Zoek en kies: Audicien - Hoorwijzer
                                </Link>
                            </Content>
                        </Article>
                    </PageSection>
                    <PageSection bgColor="white">
                        <Article>
                            <Content>
                                <ParagraphTitle>3. Bereid je voor op je afspraak</ParagraphTitle>
                                <Paragraph>
                                    Er bestaan verschillende soorten hoorhulpmiddelen. Het is belangrijk dat je alvast
                                    nadenkt over wat jij belangrijk vindt in een hoorhulpmiddel. Op de website van
                                    Stichting Hoormij vind je hierover meer informatie.
                                </Paragraph>
                                <Link
                                    color="secondary"
                                    href="https://www.stichtinghoormij.nl/items/nl-nl/faq/voor-iedereen/uit-welke-hoortoestellen-kan-ik-kiezen"
                                    iconLeft="external"
                                    openInNewTab={true}
                                    variant="externalLink"
                                >
                                    Uit welke hoortoestellen kan ik kiezen? - Stichting Hoormij
                                </Link>
                            </Content>
                        </Article>
                    </PageSection>
                </Container>
            </Main>
            <Footer />
        </PageWrapper>
    );

    async function loadHearingLossRecipe() {
        const recipes = await getHearingLossRecipes();
        const recipe = recipes.find((r) => r.processActivityId === processActivityId);
        setHearingLossRecipe(recipe);
    }

    async function downloadRecipe() {
        if (hearingLossRecipe) {
            downloadHearingLossRecipe(hearingLossRecipe.processActivityId);
        }
    }
}
